import React, { useState, useEffect, KeyboardEvent } from 'react';
import { Save, ArrowLeft, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

interface Template {
  id: string;
  name: string;
  content: string;
  status: string;
  usage: string;
  created_at: string;
  updated_at: string;
}

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
  onSuccess: () => void;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Template>>({
    name: '',
    content: '',
    status: 'draft',
    usage: 'newsletter',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isNewTemplate = !template;

  useEffect(() => {
    if (template) {
      setFormData(template);
    } else {
      setFormData({
        name: '',
        content: '',
        status: 'draft',
        usage: 'newsletter',
      });
    }
  }, [template]);

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey as any);
    return () => {
      document.removeEventListener('keydown', handleEscKey as any);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  const handleDelete = async () => {
    if (!template) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('templates')
        .delete()
        .eq('id', template.id);

      if (error) throw error;

      toast.success('Template deleted successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error deleting template: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    try {
      const dataToSubmit = {
        ...formData,
      };

      let result;
      if (isNewTemplate) {
        result = await supabase.from('templates').insert(dataToSubmit).select();
      } else {
        result = await supabase
          .from('templates')
          .update(dataToSubmit)
          .eq('id', template.id)
          .select();
      }

      if (result.error) throw result.error;

      toast.success(isNewTemplate ? 'Template created successfully' : 'Template updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className={`fixed inset-y-0 right-0 w-full md:w-1/2 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } z-50`}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {isNewTemplate ? 'Create New Template' : 'Edit Template'}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Enter template name..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Usage
              </label>
              <select
                value={formData.usage}
                onChange={(e) => setFormData({ ...formData, usage: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
              >
                <option value="newsletter">Newsletter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="chapter">Chapter</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={12}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Enter template content..."
                required
              />
            </div>

            {!isNewTemplate && !showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Template
                </button>
              </div>
            )}

            {showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    Are you sure you want to delete this template? This action cannot be undone.
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="shrink-0 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || showDeleteConfirm}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2.5 px-4 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                {isNewTemplate ? 'Create Template' : 'Update Template'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;